import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './YourHome.css';
import { getRentalStatus } from '../../../api/services/rentalService';
import { getProfile } from '../../../api/services/authService';
import PropertyRatingModal from './PropertyRatingModal';
import GuestChatModal from '../../shared/GuestChatModal/GuestChatModal';

const formatMoneyKes = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';

  // Render without decimals for whole numbers, but keep cents if present.
  const hasCents = Math.floor(num) !== num;
  const formatted = num.toLocaleString(undefined, {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  });
  return `KES ${formatted}`;
};

const formatDateShort = (isoDate) => {
  if (!isoDate) return '—';
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return String(isoDate);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
};

const formatNoticeMonthYear = (month, year) => {
  const m = Number(month);
  const y = Number(year);
  if (!Number.isFinite(m) || !Number.isFinite(y)) return '—';
  const d = new Date(y, m - 1, 1);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};

const YourHome = () => {
  const navigate = useNavigate();
  const [rental, setRental] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tenantCode, setTenantCode] = useState(null);
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);

  const fetchRental = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await getRentalStatus();
      if (res?.status === 'success' && res?.data) {
        setRental(res.data);
        return;
      }

      setRental(res?.data || null);
      setError(new Error(res?.message || 'Could not load rental status.'));
    } catch (e) {
      setRental(null);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRental();
  }, [fetchRental]);

  useEffect(() => {
    let cancelled = false;
    const fetchTenantCode = async () => {
      try {
        const res = await getProfile();
        const code =
          res?.status === 'success' && res?.data
            ? res.data.tenantCode ?? res.data.tenant_code ?? null
            : null;
        if (!cancelled) setTenantCode(code);
      } catch {
        if (!cancelled) setTenantCode(null);
      }
    };

    fetchTenantCode();
    return () => {
      cancelled = true;
    };
  }, []);

  const unit = rental?.unit || null;
  const amenitiesPreview = useMemo(
    () => (Array.isArray(unit?.amenities) ? unit.amenities.slice(0, 6) : []),
    [unit]
  );
  const leaseEndLabel = rental?.endDate ? formatDateShort(rental.endDate) : 'Ongoing';
  const noticeStatus = String(rental?.noticeStatus || '').toUpperCase();
  const showNotice = Boolean(rental?.onNotice) || noticeStatus === 'PENDING' || noticeStatus === 'APPROVED';
  const noticeInitiatedBy = String(rental?.noticeInitiatedBy || '').replace(/_/g, ' ').trim() || '—';
  const noticeMoveOutDate = formatNoticeMonthYear(rental?.noticeMoveOutMonth, rental?.noticeMoveOutYear);

  return (
    <div className="your-home-page">
      <div className="your-home-content rental-status-content">
        {loading && (
          <div className="rent-loading-card">
            <div className="rent-loading-title">Loading rental status…</div>
            <div className="rent-loading-subtitle">Fetching your unit details.</div>
          </div>
        )}

        {!loading && error && (
          <div className="rent-error-card">
            <div className="rent-error-title">Could not load rental status</div>
            <div className="rent-error-message">{error.message || 'Please try again.'}</div>
            <button className="rent-retry-btn" type="button" onClick={fetchRental}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && rental && (
          <>
            {/* Hero / Summary Card */}
            <div className="your-home-property-card rental-hero-card">
              <div className="property-card-content rental-hero-content">
                <h2 className="property-card-title rental-hero-title">
                  {unit?.unitName || unit?.unitNumber || 'Your Rental'}
                </h2>
                <p className="property-card-location">
                  {rental?.propertyName ? `Property: ${rental.propertyName}` : 'Your property'}
                </p>
                {tenantCode ? <div className="property-card-tenant-code">Tenant Code: {tenantCode}</div> : null}

                <div className="rental-hero-metrics">
                  <div className="rental-metric">
                    <div className="rental-metric-label">Monthly Rent</div>
                    <div className="rental-metric-value">{formatMoneyKes(unit?.monthlyRent)}</div>
                  </div>
                  <div className="rental-metric">
                    <div className="rental-metric-label">Deposit</div>
                    <div className="rental-metric-value">{formatMoneyKes(unit?.depositAmount)}</div>
                  </div>
                </div>

                <div className="rental-hero-actions">
                  <button 
                    className="rate-property-btn" 
                    type="button"
                    onClick={() => setIsRatingModalOpen(true)}
                  >
                    Rate this Property
                  </button>
                  <button
                    className="rate-property-btn rate-property-btn--secondary"
                    type="button"
                    onClick={() => navigate('/tenant/tenancy-history')}
                  >
                    View my tenancy history
                  </button>
                  <button
                    className="rate-property-btn tenant-home-chat-btn"
                    type="button"
                    onClick={() => setIsChatModalOpen(true)}
                    disabled={!rental?.propertyId}
                  >
                    Chat with landlord
                  </button>
                </div>
              </div>
            </div>

            {/* Important Details Only */}
            <div className="details-grid rental-details-grid">
              <div className="detail-section">
                <div className="detail-label">Rent Due</div>
                <div className="detail-value">{unit?.rentDueDay ? `Day ${unit.rentDueDay}` : '—'}</div>
              </div>

              <div className="detail-section">
                <div className="detail-label">Lease Start</div>
                <div className="detail-value">{formatDateShort(rental?.startDate)}</div>
              </div>

              <div className="detail-section">
                <div className="detail-label">Lease End</div>
                <div className="detail-value">{leaseEndLabel}</div>
              </div>

              <div className="detail-section">
                <div className="detail-label">Unit</div>
                <div className="detail-value">
                  {unit?.unitNumber ? unit.unitNumber : '—'}
                  {unit?.unitTypeName ? ` • ${unit.unitTypeName}` : ''}
                </div>
              </div>
            </div>

            {showNotice && (
              <div className="your-home-notice-card">
                <div className="your-home-notice-card__header">
                  <h3>Notice Received</h3>
                  <span className={`your-home-notice-badge your-home-notice-badge--${noticeStatus.toLowerCase() || 'pending'}`}>
                    {noticeStatus || 'PENDING'}
                  </span>
                </div>
                <div className="your-home-notice-grid">
                  <div>
                    <div className="your-home-notice-label">Initiated by</div>
                    <div className="your-home-notice-value">{noticeInitiatedBy}</div>
                  </div>
                  <div>
                    <div className="your-home-notice-label">Move-out date</div>
                    <div className="your-home-notice-value">{noticeMoveOutDate}</div>
                  </div>
                  <div className="your-home-notice-grid__full">
                    <div className="your-home-notice-label">Reason</div>
                    <div className="your-home-notice-value">{rental?.noticeReason || 'No reason provided.'}</div>
                  </div>
                </div>
              </div>
            )}

            {amenitiesPreview.length > 0 && (
              <div className="amenities-card">
                <div className="amenities-title">Amenities</div>
                <div className="amenities-chips">
                  {amenitiesPreview.map((a) => (
                    <span key={a} className="amenity-chip">
                      {a.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <PropertyRatingModal 
              isOpen={isRatingModalOpen}
              onClose={() => setIsRatingModalOpen(false)}
              propertyId={rental?.propertyId}
              propertyName={rental?.propertyName}
            />

            <GuestChatModal
              isOpen={isChatModalOpen}
              onClose={() => setIsChatModalOpen(false)}
              propertyId={rental?.propertyId}
              guestName="Landlord"
              propertySubtitle={rental?.propertyName || 'Current property'}
              chatMode="landlord"
            />
          </>
        )}
      </div>
    </div>
  );
};

export default YourHome;
